import React from 'react'
import i18next from 'i18next'
import {Menu, Typography} from 'antd'
import {ColumnsType} from 'antd/es/table'
import {EpayPayment} from '@/api'
import {DateTimeLabel, IconTitle, PaymentMethodOrMaskedCard, TableStatus} from '@/components'
import {OnlinePaymentStatus, PaymentMethodType} from '@/constants'
import {ActionButtons} from '@/pages/components'
import {TOnlinePaymentsStore} from '@/pages/OnlinePayments'
import {
    getOnlinePaymentStatusIconType,
    getOnlinePaymentStatusName,
    getOnlinePaymentStatusTagType
} from '@/pages/OnlinePayments/services'
import {getAmountWithCurrency} from '@/utils'
import {urls} from '@/constants/urls'
import {hasPermissions} from '@/stores/auth/services/utils'
import {PermissionsMap} from '@/stores/auth/constants/permissions-map'
import {TransactionType} from '@/types/transactions'
const renderActionsMenu = (object: EpayPayment, transactionsStore: TOnlinePaymentsStore) => {
    switch (object.status) {
        case OnlinePaymentStatus.auth:
            return (
                <ActionButtons
                    menu={
                        <Menu
                            items={[
                                ...(hasPermissions([PermissionsMap.online_payments.full])
                                    ? [
                                          {
                                              key: '1',
                                              label: (
                                                  <a
                                                      onClick={(e) => {
                                                          e.stopPropagation()
                                                          transactionsStore.openCancelPaymentModal(
                                                              object
                                                          )
                                                      }}
                                                  >
                                                      {i18next.t('OnlinePaymentsStore.actions.cancel')}
                                                  </a>
                                              )
                                          },
                                          {
                                              key: '2',
                                              label: (
                                                  <a
                                                      onClick={(e) => {
                                                          e.stopPropagation()
                                                          transactionsStore.openPaymentModal(object)
                                                      }}
                                                  >
                                                      {i18next.t('OnlinePaymentsStore.actions.charge')}
                                                  </a>
                                              )
                                          }
                                      ]
                                    : []),
                                {
                                    key: '3',
                                    label: (
                                        <a
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                transactionsStore.openDrawer(object)
                                            }}
                                        >
                                            {i18next.t('OnlinePaymentsStore.viewDetails')}
                                        </a>
                                    )
                                }
                            ]}
                        />
                    }
                />
            )
        case OnlinePaymentStatus.charge:
            const isRefundDisabled =
                object.paymentMethod === PaymentMethodType.openbanking &&
                !object.additionalDetails?.refundSupported

            return (
                <ActionButtons
                    menu={
                        <Menu
                            items={[
                                ...(hasPermissions([PermissionsMap.online_payments.full]) &&
                                object.balance > 0
                                    ? [
                                          {
                                              key: '1',
                                              disabled: isRefundDisabled,
                                              label: (
                                                  <a
                                                      onClick={(e) => {
                                                          e.stopPropagation()
                                                          if (!isRefundDisabled) {
                                                              transactionsStore.openPaymentModal(
                                                                  object
                                                              )
                                                          }
                                                      }}
                                                  >
                                                      {i18next.t('OnlinePaymentsStore.actions.refund')}
                                                  </a>
                                              )
                                          }
                                      ]
                                    : []),
                                {
                                    key: '2',
                                    label: (
                                        <a
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                transactionsStore.openDrawer(object)
                                            }}
                                        >
                                            {i18next.t('OnlinePaymentsStore.viewDetails')}
                                        </a>
                                    )
                                }
                            ]}
                        />
                    }
                />
            )
        default:
            return (
                <ActionButtons
                    menu={
                        <Menu
                            items={[
                                {
                                    key: '1',
                                    label: (
                                        <a onClick={() => transactionsStore.openDrawer(object)}>
                                            {i18next.t('OnlinePaymentsStore.viewDetails')}
                                        </a>
                                    )
                                }
                            ]}
                        />
                    }
                />
            )
    }
}

export const getColumns = (transactionsStore: TOnlinePaymentsStore): ColumnsType<EpayPayment> => {
    return [
        {
            title: i18next.t('OnlinePaymentsStore.columns.amount'),
            dataIndex: 'amount',
            key: 'amount',
            align: 'right' as any,
            width: 150,
            render: (item, object) => <b>{getAmountWithCurrency(item, object.currency)}</b>
        },
        {
            title: () => (
                <IconTitle
                    title={i18next.t('OnlinePaymentsStore.columns.status')}
                    tooltipText={
                        <span>
                            {i18next.t('OnlinePaymentsStore.columns.statusTooltipPrefix')}
                            {urls.paymentStatusesGuide ? (
                                <>
                                    {' '}
                                    <a
                                        className='tooltip-inline-link'
                                        href={urls.paymentStatusesGuide}
                                        target='_blank'
                                        rel='noreferrer'
                                    >
                                        {i18next.t('OnlinePaymentsStore.columns.statusTooltipLink')}
                                    </a>
                                </>
                            ) : null}
                        </span>
                    }
                />
            ),
            dataIndex: 'status',
            key: 'status',
            align: 'left' as any,
            width: 150,
            render: (item, object) => {
                if (
                    object.status === OnlinePaymentStatus.credited &&
                    object.transactionType === TransactionType.ADJUSTMENT
                ) {
                    return (
                        <TableStatus
                            statusProps={{
                                status: getOnlinePaymentStatusName(item),
                                type: getOnlinePaymentStatusTagType(item),
                                iconType: getOnlinePaymentStatusIconType(OnlinePaymentStatus.refund)
                            }}
                        />
                    )
                }
                return (
                    <TableStatus
                        statusProps={{
                            status: getOnlinePaymentStatusName(item),
                            type: getOnlinePaymentStatusTagType(item),
                            iconType: getOnlinePaymentStatusIconType(item)
                        }}
                    />
                )
            }
        },
        {
            title: i18next.t('OnlinePaymentsStore.columns.customer'),
            dataIndex: 'payerName',
            key: 'payerName',
            render: (item) => (item ? item : '-')
        },
        {
            title: i18next.t('OnlinePaymentsStore.columns.paymentMethod'),
            dataIndex: 'cardMask',
            key: 'cardMask',
            render: (item, object) => (
                <PaymentMethodOrMaskedCard
                    paymentMethod={object.paymentMethod}
                    cardScheme={object.cardType}
                    cardMask={object.cardMask}
                />
            )
        },
        {
            title: i18next.t('OnlinePaymentsStore.columns.orderNumber'),
            dataIndex: 'invoiceId',
            key: 'invoiceId',
            render: (item) =>
                item ? (
                    <Typography.Text copyable={true} className='nowrap'>
                        {item}
                    </Typography.Text>
                ) : (
                    '-'
                )
        },
        {
            title: i18next.t('OnlinePaymentsStore.columns.date'),
            dataIndex: 'createdDate',
            key: 'createdDate',
            render: (item) => <DateTimeLabel date={item} />
        },
        {
            title: i18next.t('OnlinePaymentsStore.columns.action'),
            width: 120,
            key: 'x',
            render: (item, object) => renderActionsMenu(object, transactionsStore)
        }
    ]
}

export const removePercentSymbolFromStrings = (object: {[key: string]: any}) => {
    if (!object) {
        return {}
    }
    const replaceAll = (str: string, find: string, replace: string) => {
        return str.replace(new RegExp(find, 'g'), replace)
    }

    Object.keys(object).forEach((key) => {
        const value = object[key]
        if (typeof value === 'string') {
            object[key] = replaceAll(value, '%', '')
        }
    })

    return object
}
